import { logD, logE } from "@blacknut/logging/dist";
import { PlayerEvents, eventEmitter } from "./EventEmitter";

const PING_TIMEOUT = 30000;
export interface WebSocketClientOptions {
  url: string;
  namespace: string;
  onClose?: (client: WebSocketClient) => void;
  onOpen?: (client: WebSocketClient) => void;
  onMessage?: (event: MessageEvent) => void;
}

const TRACE_EXCHANGE = false;

export class WebSocketClient {
  private retry: boolean;
  private timeout: NodeJS.Timeout | undefined;
  private pingFunction: NodeJS.Timeout | undefined;
  private url: string;
  private ws: WebSocket | undefined;

  private options: WebSocketClientOptions;

  private static SEED = 0;
  private _id: string;

  private dataSentAtLeastOnce = false;

  constructor(options: WebSocketClientOptions) {
    const { url: _url, namespace } = options;
    WebSocketClient.SEED++;
    this._id = `${namespace}#${WebSocketClient.SEED}`;
    const url = new URL(_url);
    const { hostname, port, protocol, pathname, search } = url;
    this.url = `${protocol}//${hostname}:${port}${pathname}${search}`;
    this.options = options;

    this.retry = true;
    this.connect(this.url, namespace);
  }

  private namespace: string;

  public get id(): string {
    return this._id;
  }

  private connect(url: string, namespace: string) {
    this.namespace = namespace;
    this.ws = new WebSocket(url, namespace);
    this.ws.onopen = (e) => this.onOpen(e);
    this.ws.onerror = (e) => this.onError(e);
    this.ws.onclose = () => this.onClose(); // why don't we handle the event here?
    this.ws.onmessage = (e) => this.onMessage(e);
  }

  private onOpen(event: Event) {
    logD(this.id, `onOpen %o`, {
      event,
    });
    this.options?.onOpen && this.options.onOpen(this);
    this.pingFunction = setInterval(() => {
      this.sendData("ping;");
    }, PING_TIMEOUT);
  }

  private onError(event: Event) {
    logE(this.id, `onError %o, retry=%o`, event, this.retry);
    //Not sure retry is still used
    if (this.retry) {
      this.timeout = setTimeout(() => {
        logD(this._id, `Retry`);
        this.connect(this.url, this.namespace);
      }, 10000);
    } else {
      eventEmitter.emit(PlayerEvents.ERROR, event);
    }
  }

  private forceClose() {
    logD(this._id, `forceClose`);
    if (this.ws) {
      logD(this.id, "Removing WS listeners");
      this.ws.onclose = () => {
        logD(this._id, "Closed");
      };
      this.ws.onerror = undefined;
      this.ws.onopen = undefined;
    }
    this.ws.close(1000, "forceClose");
    this.ws = undefined;
    this.retry = false;
    clearTimeout(this.timeout);
    clearTimeout(this.pingFunction);
    this.timeout = undefined;
    this.pingFunction = undefined;
  }
  private onClose() {
    logD(this._id, `onCloseNormaly`);
    this.forceClose();
    this.options.onClose && this.options.onClose(this);
  }
  private onMessage(event: MessageEvent) {
    this.options?.onMessage && this.options.onMessage(event);
  }
  sendData(data: string) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (this.dataSentAtLeastOnce) {
        logE(this._id, `Sending failed %o`, data);
      } else {
        logD(this._id, `Sending failed %o`, data);
      }
      return false;
    }
    TRACE_EXCHANGE && logD(this.id, "Sending data: %o", data);

    this.ws.send(data);
    this.dataSentAtLeastOnce = true;
    return true;
  }

  public stop(reconnect: boolean) {
    logD(this.id, `stop. Can reconnect: %o`, reconnect);
    if (!reconnect) {
      this.sendData("exit|0;");
      this.ws?.close(1000);
      this.retry = false;
    }
    this.forceClose();
  }
}
